import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import FaqPage from './pages/FaqPage';
import PrivacyPage from './pages/PrivacyPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import SummitSubmissionPage from './pages/SummitSubmissionPage';
import EventsPage from './pages/EventsPage';
import PremiumPage from './pages/PremiumPage';
import PremiumSuccessPage from './pages/PremiumSuccessPage';
import MapperPage from './pages/MapperPage';
import OpenAppPage from './pages/OpenAppPage';
import { LanguageProvider } from './context/LanguageContext';
import { isWebView } from './utils/webview';
import { detectPreferredLanguage } from './utils/language';

// Component to handle root route language detection
const RootRedirect: React.FC = () => {
  const preferredLang = detectPreferredLanguage();
  return <Navigate to={`/${preferredLang}`} replace />;
};

// Component to handle events route language detection while preserving event ID
const EventsRedirect: React.FC = () => {
  const preferredLang = detectPreferredLanguage();
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/${preferredLang}/events/${id}`} replace />;
};

// Component to handle /mapper route - redirect to preferred language
const MapperRedirect: React.FC = () => {
  const preferredLang = detectPreferredLanguage();
  return <Navigate to={`/${preferredLang}/mapper`} replace />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <Layout>
          <Routes>
            {/* Root route - detect and redirect to preferred language */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/open" element={<OpenAppPage />} />
            
            {/* Events route without language - detect and redirect to preferred language */}
            <Route path="/events/:id" element={<EventsRedirect />} />
            {/* Mapper route without language - redirect to preferred language */}
            <Route path="/mapper" element={<MapperRedirect />} />
            
            {/* Language-specific routes */}
            <Route path="/:lang" element={<LandingPage />} />
            <Route 
              path="/:lang/faq" 
              element={<FaqPage showNavigation={!isWebView()} />} 
            />
            <Route 
              path="/:lang/privacy" 
              element={<PrivacyPage showNavigation={!isWebView()} />} 
            />
            <Route 
              path="/:lang/account-deletion" 
              element={<AccountDeletionPage showNavigation={!isWebView()} />} 
            />
            <Route 
              path="/:lang/submit-summit" 
              element={<SummitSubmissionPage />} 
            />
            <Route 
              path="/:lang/events/:id" 
              element={<EventsPage />} 
            />
            <Route 
              path="/:lang/premium" 
              element={<PremiumPage />} 
            />
            <Route 
              path="/:lang/premium/success" 
              element={<PremiumSuccessPage />} 
            />
            <Route 
              path="/:lang/mapper" 
              element={<MapperPage />} 
            />
            
            {/* Catch all route - redirect to preferred language */}
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </Layout>
      </LanguageProvider>
    </BrowserRouter>
  );
};

export default App; 